import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import { getLabels, getLanguageName } from '../../utilities/helpers/capacitacionesLabels';

const country = process.env.REACT_APP_COUNTRY;
const WEBINARS_API_URL = process.env.REACT_APP_WEBINARS_API_URL || 'http://localhost:3000';

export const Capacitaciones = () => {
  const [capacitacionesPais, setCapacitacionesPais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroIdioma, setFiltroIdioma] = useState('todos'); // 'todos', 'espanol', 'ingles'

  useEffect(() => {
    const fetchCapacitaciones = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Fetching webinars para país:', country);
        console.log('🌐 API URL:', `${WEBINARS_API_URL}/api/webinars`);

        // ✅ Llamar al backend con filtro por país + regionales
        const res = await fetch(
          `${WEBINARS_API_URL}/api/webinars?pais=${country}&includeRegional=true&futureOnly=true&format=list`
        );

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        console.log('📦 Webinars recibidos:', data);

        // El backend devuelve { items: [...] } con formato list
        const webinars = data.items || [];
        setCapacitacionesPais(webinars);

      } catch (error) {
        console.error('❌ Error al cargar capacitaciones:', error);
        setError(error.message);
        setCapacitacionesPais([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCapacitaciones();
  }, []);

  const handleClick = (imageUrl) => {
    if (imageUrl) {
      Swal.fire({
        imageUrl: imageUrl,
        imageWidth: 1000,
        imageHeight: 500,
        imageAlt: 'Imagen del webinar',
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'No hay imagen disponible',
        text: 'Esta capacitación no tiene una imagen asociada.',
      });
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    try {
      // Backend devuelve fecha en formato ISO: "2025-10-31T00:00:00.000Z"
      const date = new Date(fecha);
      // ✅ Usar métodos UTC para evitar problemas de zona horaria
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.warn('Fecha inválida:', fecha);
      return fecha;
    }
  };

  // Función para construir URL completa de imagen
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // Si ya es una URL completa, devolverla tal cual
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Si es una ruta relativa, construir URL completa
    // El backend sirve imágenes en /uploads/nombre-archivo.jpg
    return `${WEBINARS_API_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  const renderCapacitaciones = () => {
    if (loading) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          marginTop: '2em' 
        }}>
          <Spinner animation="border" variant="primary" />
          <h2 style={{ color: 'gray', marginTop: '1em' }}>
            Cargando webinars...
          </h2>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginTop: '2em',
          padding: '2em'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: '#d32f2f', marginBottom: '1em' }}>
              ⚠️ Error al cargar capacitaciones
            </h2>
            <p style={{ color: 'gray' }}>{error}</p>
            <button 
              className="btn btn-primary mt-3"
              onClick={() => window.location.reload()}
            >
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    // ✅ Filtrar capacitaciones por idioma (solo para USA)
    const capacitacionesFiltradas = country === 'USA' && filtroIdioma !== 'todos'
      ? capacitacionesPais.filter(cap => cap.idioma === filtroIdioma)
      : capacitacionesPais;

    if (capacitacionesFiltradas.length === 0) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginTop: '2em',
          padding: '2em'
        }}>
          <h1 style={{ 
            textAlign: 'center', 
            fontSize: '2em', 
            color: 'gray' 
          }}>
            No hay capacitaciones disponibles en este momento.
          </h1>
        </div>
      );
    }

    return capacitacionesFiltradas.map((capacitacion) => {
      // Obtener las etiquetas según el idioma de la capacitación
      const labels = getLabels(capacitacion.idioma);
      const languageName = getLanguageName(capacitacion.idioma);
      const imageUrl = getImageUrl(capacitacion.imagen);

      return (
        <Card key={capacitacion.id} className="m-5">
          <Row className="g-0">
            <Col md="2">
              {imageUrl && (
                <Card.Img
                  onClick={() => handleClick(imageUrl)}
                  className="imagenCardCapacitaciones"
                  src={imageUrl}
                  alt="imagen capacitacion"
                  style={{ 
                    height: '100%', 
                    objectFit: 'cover', 
                    cursor: 'pointer' 
                  }}
                  onError={(e) => {
                    // ✅ Fallback si la imagen no carga
                    console.warn('❌ Error cargando imagen:', imageUrl);
                    e.target.src = process.env.PUBLIC_URL + '/imagenes/capacitaciones/SolutionBoxUniversity.png';
                  }}
                />
              )}
            </Col>
            <Col md="10">
              <Card.Body>
                <Card.Title className="mb-3">{capacitacion.titulo}</Card.Title>

                {/* Información en dos columnas */}
                <Row>
                  {/* Columna izquierda */}
                  <Col md="6">
                    {capacitacion.horario && (
                      <div className="mb-2">
                        <strong>{labels.horario}</strong>
                        <div className="text-muted">{capacitacion.horario}</div>
                      </div>
                    )}
                    
                    {capacitacion.fecha && (
                      <div className="mb-2">
                        <strong>{labels.fecha}</strong>
                        <div className="text-muted">{formatFecha(capacitacion.fecha)}</div>
                      </div>
                    )}
                    
                    {capacitacion.ubicacion && (
                      <div className="mb-2">
                        <strong>{labels.ubicacion}</strong>
                        <div className="text-muted">{capacitacion.ubicacion}</div>
                      </div>
                    )}
                  </Col>
                  
                  {/* Columna derecha */}
                  <Col md="6">
                    {capacitacion.marca && (
                      <div className="mb-2">
                        <strong>{labels.marca}</strong>
                        <div className="text-muted">{capacitacion.marca}</div>
                      </div>
                    )}
                    
                    {capacitacion.responsable && (
                      <div className="mb-2">
                        <strong>{labels.responsable}</strong>
                        <div className="text-muted">{capacitacion.responsable}</div>
                      </div>
                    )}
                    
                    {capacitacion.idioma && (
                      <div className="mb-2">
                        <strong>{labels.idioma}</strong>
                        <div className="text-muted">{languageName}</div>
                      </div>
                    )}
                  </Col>
                </Row>
                
                {/* Botón de inscripción */}
                {capacitacion.inscripcionLink && (
                  <div className="mt-3">
                    <a 
                      href={capacitacion.inscripcionLink} 
                      className="butnCapacitaciones btn btn-primary btn-lg px-4"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {labels.inscribirse}
                    </a>
                  </div>
                )}
              </Card.Body>
            </Col>
          </Row>
        </Card>
      );
    });
  };

  return (
    <div>
      <img 
        src={process.env.PUBLIC_URL + "/banner/capacitaciones.jpg"} 
        alt="Imagen capacitaciones" 
        className='imagenesBody' 
        loading='lazy' 
      />
      
      {/* Filtro de idioma - solo para USA */}
      {country === 'USA' && !loading && !error && capacitacionesPais.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2em 0 1em 0',
          gap: '1em'
        }}>
          <button
            onClick={() => setFiltroIdioma('espanol')}
            style={{
              padding: '0.75em 2em',
              fontSize: '1.1em',
              fontWeight: '600',
              border: filtroIdioma === 'espanol' ? '3px solid red' : '2px solid #ccc',
              borderRadius: '8px',
              backgroundColor: filtroIdioma === 'espanol' ? '#fff' : '#f8f9fa',
              color: filtroIdioma === 'espanol' ? '#000' : '#6c757d',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: filtroIdioma === 'espanol' ? '0 2px 8px rgba(255,0,0,0.2)' : 'none'
            }}
          >
            Español
          </button>
          
          <button
            onClick={() => setFiltroIdioma('ingles')}
            style={{
              padding: '0.75em 2em',
              fontSize: '1.1em',
              fontWeight: '600',
              border: filtroIdioma === 'ingles' ? '3px solid red' : '2px solid #ccc',
              borderRadius: '8px',
              backgroundColor: filtroIdioma === 'ingles' ? '#fff' : '#f8f9fa',
              color: filtroIdioma === 'ingles' ? '#000' : '#6c757d',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: filtroIdioma === 'ingles' ? '0 2px 8px rgba(255,0,0,0.2)' : 'none'
            }}
          >
            English
          </button>
        </div>
      )}
      
      {renderCapacitaciones()}
    </div>
  );
};